/**
 * Standardized conversion events — Facebook standard events mapped to their GA4
 * recommended-event and Reddit-Pixel equivalents — fired at funnel milestones so marketing can
 * track the flow (signup → checkout → purchase → registration) end to end.
 *
 * Every helper is consent-gated for free: it calls `window.fbq` / `window.gtag` / `window.rdt`,
 * which exist (and, for Reddit, are gated) ONLY once marketing consent loaded the SDKs (see this
 * folder's loaders). It also bails for the prerender bot. So these are safe to call from anywhere
 * — thunks, redux listeners, React effects — and simply no-op when there's no consent.
 *
 * FB ⇄ GA4 ⇄ Reddit name mapping:
 *   Lead ⇄ generate_lead ⇄ Lead · (Login custom) ⇄ login ⇄ Custom(Login) ·
 *   InitiateCheckout ⇄ begin_checkout ⇄ AddToCart · AddPaymentInfo ⇄ add_payment_info ⇄ Custom(AddPaymentInfo) ·
 *   Purchase ⇄ purchase ⇄ Purchase · Subscribe ⇄ subscribe (GA4 custom) ⇄ Custom(Subscribe) ·
 *   CompleteRegistration ⇄ sign_up ⇄ SignUp
 */

import { rdtTrack, rdtCustom } from './redditPixel';
import { trackerSafeUrl } from './sanitize';

type Params = Record<string, unknown>;

/** FB `content_type` for the brand-creation purchase funnel. */
const CONTENT_TYPE = 'casino_setup';

const skip = (): boolean => Boolean(window.config?.inPrerender);
// fbevents / rdt auto-attach the full live URL to every event with no override, so any fbq / rdt
// call made while a sensitive param (e.g. the magic-link token) or Stats > Website's filters are in
// the URL would leak them, so skip rather than leak (GA still records the event, with a scrubbed URL).
const urlSafe = trackerSafeUrl;
const fbTrack = (event: string, params?: Params): void => {
  if (!urlSafe()) return;
  params ? window.fbq?.('track', event, params) : window.fbq?.('track', event);
};
const fbCustom = (event: string, params?: Params): void => {
  if (!urlSafe()) return;
  params ? window.fbq?.('trackCustom', event, params) : window.fbq?.('trackCustom', event);
};
const gaEvent = (event: string, params?: Params): void =>
  params ? window.gtag?.('event', event, params) : window.gtag?.('event', event);
// rdt is gated on both consent (inside `rdtTrack`) and URL-safety (here, since it snapshots the URL).
const rdtEvent = (event: string, params?: Params): void => {
  if (!urlSafe()) return;
  rdtTrack(event, params);
};
const rdtCustomEvent = (name: string, params?: Params): void => {
  if (!urlSafe()) return;
  rdtCustom(name, params);
};

/** A GA4 `items` entry describing the brand-setup line (GA4 revenue events expect an items array). */
const setupItems = (value: number, contentIds: string[]): Params[] => [
  {
    item_id: contentIds[0] ?? CONTENT_TYPE,
    item_name: 'Casino setup',
    item_category: CONTENT_TYPE,
    price: value,
    quantity: 1,
  },
];

/** A Reddit `products` entry describing the brand-setup line (Reddit revenue events expect one). */
const setupProducts = (contentIds: string[]): Params[] => [
  { id: contentIds[0] ?? CONTENT_TYPE, name: 'Casino setup', category: CONTENT_TYPE },
];

export interface CheckoutParams {
  value: number;
  currency: string;
  contentIds?: string[];
  numItems?: number;
}

export interface PurchaseParams {
  transactionId: string;
  value: number;
  currency: string;
  contentIds?: string[];
}

export interface SubscribeParams {
  value: number;
  currency: string;
  predictedLtv?: number;
}

/** New prospect entered the funnel — a new owner let into the configurator. */
export function trackLead(): void {
  if (skip()) return;
  fbTrack('Lead', { content_category: 'configurator' });
  gaEvent('generate_lead');
  rdtEvent('Lead');
}

/** Returning owner completed sign-in. FB / Reddit have no standard Login event → a custom one. */
export function trackLogin(method: string): void {
  if (skip()) return;
  fbCustom('Login', { method });
  gaEvent('login', { method });
  rdtCustomEvent('Login', { method });
}

/** Reached checkout (the payment step). Reddit's closest standard is AddToCart. */
export function trackInitiateCheckout({ value, currency, contentIds = [], numItems = 1 }: CheckoutParams): void {
  if (skip()) return;
  fbTrack('InitiateCheckout', {
    value,
    currency,
    content_ids: contentIds,
    content_type: CONTENT_TYPE,
    num_items: numItems,
  });
  gaEvent('begin_checkout', { value, currency, items: setupItems(value, contentIds) });
  rdtEvent('AddToCart', { value, currency, itemCount: numItems, products: setupProducts(contentIds) });
}

/** Submitted payment details (Stripe confirm). Reddit has no standard for it → a custom one. */
export function trackAddPaymentInfo({ value, currency, contentIds = [] }: CheckoutParams): void {
  if (skip()) return;
  fbTrack('AddPaymentInfo', { value, currency, content_ids: contentIds, content_type: CONTENT_TYPE });
  gaEvent('add_payment_info', { value, currency, items: setupItems(value, contentIds) });
  rdtCustomEvent('AddPaymentInfo', { value, currency, products: setupProducts(contentIds) });
}

/** Completed a purchase — the brand-creation payment; `value` = total due today. */
export function trackPurchase({ transactionId, value, currency, contentIds = [] }: PurchaseParams): void {
  if (skip()) return;
  fbTrack('Purchase', { value, currency, content_ids: contentIds, content_type: CONTENT_TYPE });
  gaEvent('purchase', { transaction_id: transactionId, value, currency, items: setupItems(value, contentIds) });
  rdtEvent('Purchase', { value, currency, transactionId, itemCount: 1, products: setupProducts(contentIds) });
}

/**
 * Started a recurring subscription — the monthly product features; `value` = monthly amount.
 * GA4 has no standard `subscribe` (purchase covers the standard revenue event) and Reddit has
 * none either, so both get a custom event alongside FB's standard Subscribe.
 */
export function trackSubscribe({ value, currency, predictedLtv }: SubscribeParams): void {
  if (skip()) return;
  fbTrack('Subscribe', { value, currency, predicted_ltv: predictedLtv ?? value * 12 });
  gaEvent('subscribe', { value, currency });
  rdtCustomEvent('Subscribe', { value, currency });
}

/** Completed registration — post-purchase, at the "check your email" screen. */
export function trackCompleteRegistration(method = 'email'): void {
  if (skip()) return;
  fbTrack('CompleteRegistration', { status: true });
  gaEvent('sign_up', { method });
  rdtEvent('SignUp');
}

/**
 * Run `fn` once per unique `key`, ever (persisted in `localStorage`). Used to fire
 * Purchase / Subscribe / CompleteRegistration exactly once per session across reloads
 * (a 3DS redirect return, or the WebSocket success followed by a manual reload). Fails
 * OPEN across reloads when storage is unavailable (fires anyway): a possible duplicate is
 * better than a lost conversion. It still fires once per page load, as surfaces share a key.
 * Returns whether `fn` ran, for callers that report something else on a repeat.
 */
const FIRED_PREFIX = 'ows-analytics-fired:';
const firedThisLoad = new Set<string>();
export function trackOnce(key: string, fn: () => void): boolean {
  const storageKey = FIRED_PREFIX + key;
  if (firedThisLoad.has(storageKey)) return false;
  try {
    if (localStorage.getItem(storageKey)) return false;
  } catch {
    /* storage unavailable — fall through and fire */
  }
  firedThisLoad.add(storageKey);
  fn();
  try {
    localStorage.setItem(storageKey, '1');
  } catch {
    /* ignore — dedup degrades to best-effort */
  }
  return true;
}
